/**
 * Persistent ometrics visitor id (`ometricsID<masterKey>` cookie with
 * localStorage fallback). Shared by chat (`sessionId` / chat_tracker),
 * conversion tracking (`conversion_tracker`), and billing page views.
 */

import { getCookie, setCookie } from './cookies.js'

const VISITOR_ID_TTL_MS = 30 * 24 * 60 * 60 * 1000

export function visitorCookieName(masterKey) {
  return `ometricsID${masterKey}`
}

export function visitorStorageKey(masterKey) {
  return `ometricsID-${masterKey}`
}

function setLsWithExpiry(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ id: value, ts: Date.now() }))
    return true
  } catch {
    return false
  }
}

function getLsWithExpiry(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data?.id || !data?.ts) {
      localStorage.removeItem(key)
      return null
    }
    if (Date.now() - data.ts > VISITOR_ID_TTL_MS) {
      localStorage.removeItem(key)
      return null
    }
    return data.id
  } catch {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
    return null
  }
}

/**
 * Read an existing visitor id without creating one.
 */
export function resolveOmetricsVisitorId(masterKey) {
  if (!masterKey || typeof document === 'undefined') {
    return undefined
  }

  const fromCookie = getCookie(visitorCookieName(masterKey))
  if (fromCookie) return fromCookie

  return getLsWithExpiry(visitorStorageKey(masterKey)) || undefined
}

/**
 * @returns {{ id: string, wasJustCreated: boolean } | null}
 */
export function ensureOmetricsVisitorId(masterKey) {
  if (!masterKey || typeof document === 'undefined') {
    return null
  }

  const cookieName = visitorCookieName(masterKey)
  const lsKey = visitorStorageKey(masterKey)

  let existing = getCookie(cookieName)
  if (existing) {
    return { id: existing, wasJustCreated: false }
  }

  existing = getLsWithExpiry(lsKey)
  if (existing) {
    return { id: existing, wasJustCreated: false }
  }

  const id =
    Math.random().toString(36).substring(2) + Date.now().toString(36)
  let cookieWorked = false
  let lsWorked = false

  setCookie(cookieName, id, 30)
  if (getCookie(cookieName) === id) {
    cookieWorked = true
  }

  if (!cookieWorked) {
    if (setLsWithExpiry(lsKey, id) && getLsWithExpiry(lsKey) === id) {
      lsWorked = true
    }
  }

  if (!cookieWorked && !lsWorked) {
    return null
  }

  if (cookieWorked && !lsWorked) {
    setLsWithExpiry(lsKey, id)
  }
  if (lsWorked && !cookieWorked) {
    setCookie(cookieName, id, 30)
  }

  return { id, wasJustCreated: true }
}

function resolveMasterKeyFromConf(conf) {
  if (!conf || typeof conf !== 'object') return ''
  return String(conf.masterKey || conf.meta?.master_key || conf.meta?.masterKey || '')
    .trim()
}

/**
 * Bind the persistent visitor id onto widget conf + chat transport fields so
 * chat_tracker and conversion_tracker share the same user_id (v1 parity).
 */
export function bindVisitorIdToConf(conf) {
  if (!conf || typeof conf !== 'object') return conf

  const masterKey = resolveMasterKeyFromConf(conf)
  if (!masterKey) return conf

  const visitor = ensureOmetricsVisitorId(masterKey)
  if (!visitor?.id) return conf

  return {
    ...conf,
    sessionId: visitor.id,
    userId: visitor.id,
    uid: visitor.id,
    user_id: visitor.id,
  }
}
